import React from 'react';
import { Helmet } from 'react-helmet';
import { Container } from 'react-bootstrap';
import { Link, useParams, Navigate } from 'react-router-dom';
import blogPosts from 'data/blogPosts';
import NavbarStandard from '../landing/NavbarStandard';
import FooterStandard from '../landing/FooterStandard';
import 'assets/css/landing-overhaul.css';

/* Lightweight Markdown-ish renderer */
const renderContent = text => {
  const lines = text.split('\n');
  const elements = [];
  // eslint-disable-next-line no-unused-vars
  let inList = false;
  let listItems = [];

  const flushList = () => {
    if (listItems.length) {
      elements.push(
        <ul key={`ul-${elements.length}`}>
          {listItems.map((li, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inlineFmt(li) }} />
          ))}
        </ul>
      );
      listItems = [];
      inList = false;
    }
  };

  const inlineFmt = s =>
    s
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>');

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }

    if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(<h2 key={i}>{trimmed.slice(3)}</h2>);
    } else if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(<h3 key={i}>{trimmed.slice(4)}</h3>);
    } else if (trimmed.startsWith('> ')) {
      flushList();
      elements.push(
        <blockquote key={i}>
          <p
            dangerouslySetInnerHTML={{ __html: inlineFmt(trimmed.slice(2)) }}
          />
        </blockquote>
      );
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      inList = true;
      listItems.push(trimmed.slice(2));
    } else if (trimmed === '---') {
      flushList();
      elements.push(<hr key={i} />);
    } else {
      flushList();
      elements.push(
        <p key={i} dangerouslySetInnerHTML={{ __html: inlineFmt(trimmed) }} />
      );
    }
  });

  flushList();
  return elements;
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  const pageUrl = `https://widestnet.web.app/blog/${post.slug}`;

  // BlogPosting structured data for Google
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: post.author || 'Widest Net Technologies'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Widest Net Technologies',
      url: 'https://widestnet.web.app'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl
    },
    keywords: (post.keywords || []).join(', '),
    articleSection: post.tag || 'AI & Distribution',
    wordCount: post.content ? post.content.split(/\s+/).length : 0
  };

  return (
    <div className="wn-landing">
      <Helmet>
        <title>{post.title} | Widest Net Technologies</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={(post.keywords || []).join(', ')} />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:site_name" content="Widest Net Technologies" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />

        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>

      <NavbarStandard />
      <article className="wn-blog-post">
        <Container>
          <div className="wn-blog-post-header wn-fade-in">
            <Link
              to="/blog"
              className="wn-blog-tag"
              style={{ marginBottom: 16 }}
            >
              ← Back to Blog
            </Link>
            <h1>{post.title}</h1>
            <div className="wn-blog-post-meta">
              <span>{post.author}</span>
              <span>·</span>
              <span>{post.date}</span>
              <span>·</span>
              <span>{post.readTime}</span>
            </div>
          </div>

          <div className="wn-blog-post-content wn-fade-in">
            {renderContent(post.content)}
          </div>

          {/* Inline CTA */}
          <div
            style={{
              marginTop: 48,
              padding: '40px',
              background: 'var(--wn-bg-soft)',
              borderRadius: 'var(--wn-radius-lg)',
              textAlign: 'center',
              border: '1px solid var(--wn-border)'
            }}
          >
            <h3 style={{ marginBottom: 8 }}>
              Ready to find where AI can move the needle?
            </h3>
            <p style={{ color: 'var(--wn-text-secondary)', marginBottom: 20 }}>
              Book a free 30-minute assessment — an honest look at where
              automation and ecommerce can drive real ROI.
            </p>
            <Link to="/#contact" className="wn-btn-primary">
              Book Free Assessment →
            </Link>
          </div>
        </Container>
      </article>
      <FooterStandard />
    </div>
  );
};

export default BlogPost;
